import { Position, SplitBillItem } from '@/interfaces/Bill';

export interface SplitBillItemRow {
  key: string;
  item: SplitBillItem;
  occurrence: number;
  isLocked: boolean;
}

export interface SelectableSplitBillItemRow extends SplitBillItemRow {
  isSelected: boolean;
}

/** Sum of a position's modifiers: Σ(modifier.unitPrice × modifier.quantity). Additive. */
const modifiersTotal = (position: Position): number =>
  position.modifiersV2.reduce((sum, modifier) => sum + modifier.unitPrice * modifier.quantity, 0);

/**
 * Per-unit amounts (in integer cents) for a position: the line total
 * `N·unitPrice + Σ modifiers` split evenly across the N units, with the rounding
 * remainder reconciled on the last unit so the units sum back to the line total.
 */
const unitAmountsCents = (position: Position): number[] => {
  const { quantity, unitPrice } = position;
  const lineTotalCents = Math.round((unitPrice * quantity + modifiersTotal(position)) * 100);
  const baseUnitCents = Math.floor(lineTotalCents / quantity);
  const remainderCents = lineTotalCents - baseUnitCents * quantity;

  return Array.from({ length: quantity }, (_, unit) =>
    unit === quantity - 1 ? baseUnitCents + remainderCents : baseUnitCents
  );
};

/**
 * Distribute a bill-level discount (in cents) across the units proportionally to their amounts,
 * largest-remainder method so the shares sum to exactly the discount.
 * The discount is clamped to [0, Σ units] so no unit goes negative.
 *
 * Algorithm explained:
 * The problem. The bill says "2.50 off the whole order", but the picker prices each unit separately.
 * So the discount has to be chopped into per-unit pieces that
 * - (a) are whole cents,
 * - (b) are fair — a 12.00 item absorbs more of the discount than a 5.00 item
 * — and (c) add up to exactly 2.50, not 2.49 or 2.51, otherwise the items no longer sum to the discounted
 * bill and the payment can be rejected.
 *
 * The idea (known as the largest-remainder method, same thing parliaments use to hand out seats after an election):
 *
 *  1. Compute each unit's fair share. Every unit should absorb the discount in proportion to its price: share = unitPrice × discount ÷ billTotal. These shares are exact but fractional — e.g. one unit's fair share comes out to 22.7 cents, which you can't charge.
 *  2. Round everything down. Give each unit the whole-cent part of its share (22.7 → 22). Rounding down never overshoots, but now a few cents of the discount are left undistributed — at most one cent per unit.
 *  3. Hand out the leftover cents to the units that got shortchanged the most. Sort units by the fraction that was cut off (the ".7" in 22.7). The unit that lost 0.9 of a cent is first in line, the one that lost 0.1 is last. Give one extra cent each, top of the list downward, until the leftovers run out. Ties break by position on the bill, so the result is always the same for the same input.
 *  4. Subtract. Each unit's final price is its original price minus its share.
 *
 *  Worked example. Two Cokes at 5.00 and Fries at 12.00, discount 1.00 (100 cents on a 2200-cent bill):
 *
 *  - Fair shares: Coke 22.73¢ each, Fries 54.55¢.
 *  - Floored: 22 + 22 + 54 = 98¢ — 2 cents left over.
 *  - Largest fractions: the Cokes (.73 each) beat Fries (.55), so each Coke gets one extra cent.
 *  - Final shares 23 + 23 + 54 = exactly 100. Prices: 4.77, 4.77, 11.46 — sums to 21.00 on the nose.
 */
const discountedUnitCents = (unitCents: number[], discountCents: number): number[] => {
  const totalCents = unitCents.reduce((sum, cents) => sum + cents, 0);
  const clampedDiscountCents = Math.min(Math.max(discountCents, 0), totalCents);
  if (clampedDiscountCents === 0) {
    return unitCents;
  }

  // Each unit's exact proportional share of the discount, in fractional cents
  const exactShares = unitCents.map(cents => (cents * clampedDiscountCents) / totalCents);

  // Flooring to whole cents under-distributes by up to (units − 1) cents
  const flooredShares = exactShares.map(share => Math.floor(share));
  const flooredTotal = flooredShares.reduce((sum, share) => sum + share, 0);
  const leftoverCents = clampedDiscountCents - flooredTotal;

  // The leftover cents go to the units flooring cut the most from — largest
  // fractional remainder first, index as tie-break so the result is stable
  const topUpUnitIndexes = new Set(
    exactShares
      .map((exact, unitIndex) => ({ remainder: exact - flooredShares[unitIndex], unitIndex }))
      .sort((a, b) => b.remainder - a.remainder || a.unitIndex - b.unitIndex)
      .slice(0, leftoverCents)
      .map(({ unitIndex }) => unitIndex)
  );

  return unitCents.map((cents, unitIndex) => {
    const share = flooredShares[unitIndex] + (topUpUnitIndexes.has(unitIndex) ? 1 : 0);
    return cents - share;
  });
};

/**
 * Explode a bill's positions into one SplitBillItem per ordered unit.
 *
 * A Position with `quantity: N` becomes N SplitBillItems, each carrying the
 * shared `lineItemId = Position.id` (identity is by id with multiplicity — see
 * ADR 0003). Each unit's `amount` is fully-loaded (incl. modifiers) and net of
 * its proportional share of the bill-level `grossDiscount`; the whole-bill
 * invariant is Σ(all SplitBillItem.amount) === bill.baseTotal. Computation
 * runs in integer cents to keep that sum exact under float rounding.
 */
export const explodeBillToSplitItems = (
  positions: Position[],
  grossDiscount: number = 0
): SplitBillItem[] => {
  const billablePositions = positions.filter(position => position.quantity >= 1);
  const unitCents = billablePositions.flatMap(unitAmountsCents);
  const netUnitCents = discountedUnitCents(unitCents, Math.round(grossDiscount * 100));

  let unitIndex = 0;
  return billablePositions.flatMap(position =>
    Array.from({ length: position.quantity }, () => ({
      lineItemId: position.id,
      name: position.name,
      amount: netUnitCents[unitIndex++] / 100,
      modifiers: position.modifiersV2,
    }))
  );
};

/** Collapse a lineItemId multiset (e.g. [coke, coke, fries]) into per-id unit counts. */
export const countLineItemIds = (lineItemIds: string[]): Record<string, number> =>
  lineItemIds.reduce<Record<string, number>>((counts, lineItemId) => {
    counts[lineItemId] = (counts[lineItemId] || 0) + 1;
    return counts;
  }, {});

export const buildSplitBillItemRows = (
  items: SplitBillItem[],
  lockedLineItemIds: string[]
): SplitBillItemRow[] => {
  const lockedCounts = countLineItemIds(lockedLineItemIds);
  const seenCounts: Record<string, number> = {};

  return items.map(item => {
    const occurrence = (seenCounts[item.lineItemId] || 0) + 1;
    seenCounts[item.lineItemId] = occurrence;

    return {
      key: `${item.lineItemId}:${occurrence}`,
      item,
      occurrence,
      isLocked: occurrence <= (lockedCounts[item.lineItemId] || 0),
    };
  });
};

/**
 * Tick the first `selectedUnitCounts[lineItemId]` unlocked rows of each lineItemId.
 * Units of one dish are interchangeable (count semantics, ADR 0003), so ticks always
 * fill from the top; counts beyond the available unlocked units are clamped, which
 * absorbs concurrently-locked units without a separate pruning pass.
 */
export const applySelectionToRows = (
  rows: SplitBillItemRow[],
  selectedUnitCounts: Record<string, number>
): SelectableSplitBillItemRow[] => {
  const appliedCounts: Record<string, number> = {};

  return rows.map(row => {
    if (row.isLocked) {
      return { ...row, isSelected: false };
    }

    const lineItemId = row.item.lineItemId;
    const applied = (appliedCounts[lineItemId] || 0) + 1;
    appliedCounts[lineItemId] = applied;

    return { ...row, isSelected: applied <= (selectedUnitCounts[lineItemId] || 0) };
  });
};

/** The ticked selection as the flat lineItemId multiset committed to context. */
export const selectedLineItemIdsFromRows = (rows: SelectableSplitBillItemRow[]): string[] =>
  rows.filter(row => row.isSelected).map(row => row.item.lineItemId);

/** Σ amount of ticked rows, computed in integer cents to stay float-exact. */
export const calculateSelectedAmount = (rows: SelectableSplitBillItemRow[]): number => {
  const selectedCents = rows
    .filter(row => row.isSelected)
    .reduce((total, row) => total + Math.round(row.item.amount * 100), 0);

  return selectedCents / 100;
};

/**
 * The ticked selection priced as counts, top-fill — the ONLY pricing every
 * device derives identically (ADR 0003: the backend locks counts, not units).
 * When same-dish units differ by a distribution cent, the tapped unit's own
 * amount may deviate from this by that cent; committing the tapped amount
 * instead would let two payers both claim the dearer unit and desync the
 * remaining balance. Always commit and charge THIS amount.
 */
export const calculateCountBasedAmount = (rows: SelectableSplitBillItemRow[]): number =>
  calculateSelectedAmount(
    applySelectionToRows(rows, countLineItemIds(selectedLineItemIdsFromRows(rows)))
  );

/**
 * A payment-intent line item in the wire shape the payments API accepts:
 * plain-string `id` and a required `description` (MoneyHash rejects without it).
 * See ADR 0003 (revised contract).
 */
export interface IntentLineItem {
  id: string;
  name: string;
  description: string;
  amount: number;
}

/**
 * The confirmed selection as payment-intent `lineItems` — one entry per selected
 * unit, ids repeating per multiplicity. Units fill from the top among unlocked
 * rows (same semantics as the picker), so per-entry amounts match what the picker
 * summed into the payer's share, including the last-unit rounding remainder.
 */
export const buildIntentLineItems = (
  items: SplitBillItem[],
  selectedLineItemIds: string[],
  lockedLineItemIds: string[]
): IntentLineItem[] =>
  applySelectionToRows(
    buildSplitBillItemRows(items, lockedLineItemIds),
    countLineItemIds(selectedLineItemIds)
  )
    .filter(row => row.isSelected)
    .map(row => ({
      id: row.item.lineItemId,
      name: row.item.name,
      description: row.item.name,
      amount: row.item.amount,
    }));

/**
 * Diff the confirmed selection against fresh lock state: units another payer
 * locked since Confirm fall out of the selection (clamped per id, fills from
 * the top). `hasConflict` flags that at least one selected unit was lost —
 * the pre-payment signal to abort the intent and re-open the picker. Selected
 * ids that left the bill entirely (kitchen edit) also count as conflicts.
 */
export const reconcileSelectionWithLocks = (
  items: SplitBillItem[],
  selectedLineItemIds: string[],
  lockedLineItemIds: string[]
): { selectedLineItemIds: string[]; selectedAmount: number; hasConflict: boolean } => {
  const rows = applySelectionToRows(
    buildSplitBillItemRows(items, lockedLineItemIds),
    countLineItemIds(selectedLineItemIds)
  );
  const reconciledIds = selectedLineItemIdsFromRows(rows);

  return {
    selectedLineItemIds: reconciledIds,
    selectedAmount: calculateSelectedAmount(rows),
    hasConflict: reconciledIds.length < selectedLineItemIds.length,
  };
};

/**
 * Tick exactly the rows whose key is in `tickedRowKeys`. Unlike the count-based
 * applySelectionToRows (which normalizes ticks to fill from the top), this keeps
 * the tick on the unit the payer actually tapped. Locked rows never tick, so keys
 * pointing at units locked since the tap fall away on the next projection.
 */
export const applyRowKeySelectionToRows = (
  rows: SplitBillItemRow[],
  tickedRowKeys: ReadonlySet<string>
): SelectableSplitBillItemRow[] =>
  rows.map(row => ({ ...row, isSelected: !row.isLocked && tickedRowKeys.has(row.key) }));

/**
 * Ticked row keys after toggling `row`. Rebuilt from the effective (locked-pruned)
 * ticks visible in `rows`, so a tick lost to concurrent locking cannot resurface.
 */
export const toggleRowKeySelection = (
  rows: SelectableSplitBillItemRow[],
  row: SelectableSplitBillItemRow
): Set<string> => {
  const tickedRowKeys = new Set(rows.filter(r => r.isSelected).map(r => r.key));
  if (tickedRowKeys.has(row.key)) {
    tickedRowKeys.delete(row.key);
  } else {
    tickedRowKeys.add(row.key);
  }

  return tickedRowKeys;
};
